import { useState, useEffect } from "react";
import { Card, Row, Col, ProgressBar } from "react-bootstrap";
import CopyableText from "../generic/CopyableText";
import CustomTooltip from "../generic/CustomTooltip";
import "@fortawesome/fontawesome-free/css/all.min.css";
import RestartModal from "../modals/RestartModal";
import ScaleModal from "../modals/ScaleModal";
import LogsModal from "../modals/LogsModal";
import LogsViewer from "../logs/LogsViewer";

const PodInfo = ({
  setShowPodInfo,
  podInfo,
  dataLoaded,
  cluster,
  refreshPodData,
}) => {
  const [isLoaded, setIsLoaded] = useState(false); // State to control component loading
  const [selectedContainer, setSelectedContainer] = useState(0); // State to control selected container - default to first in list
  const [showRestartModal, setShowRestartModal] = useState(false); // State to control modal visibility
  const [showScaleModal, setShowScaleModal] = useState(false); // State to control modal visibility
  const [showLogsModal, setShowLogsModal] = useState(false); // State to control modal visibility
  //const [logsRefreshing, setLogsRefreshing] = useState(false); // State to control logs refreshing
  //const [logInfo, setLogInfo] = useState(podInfo.containers[selectedContainer].logs); // State to store log data

  console.log("Pod Info data: ", podInfo);

  // const getLogs = async (containerName) => {
  //     setLogsRefreshing(true);
  //     if (containerName) {
  //         try {
  //             const res = await fetch(
  //                 `/logs?cluster=dev&namespace=238240-dev&podName=${podInfo.pod_name}&container=${selectedContainer}`
  //             );
  //             const data = await res.json();

  //             const response = await axiosInstance.get(`${api_url}/logs?cluster=dev&namespace=238240-dev&podName=${podInfo.pod_name}&container=${selectedContainer}`);
  //             setLogsRefreshing(false);
  //             setLogInfo(data);
  //         } catch (error) {
  //             console.error(error);
  //             setLogsRefreshing(false);
  //         }
  //     }
  // };

  useEffect(() => {
    if (dataLoaded) {
      setIsLoaded(true);
    }
  }, [dataLoaded]);

  if (!isLoaded)
    return (
      <Card>
        <Card.Body>Loading...</Card.Body>
      </Card>
    );
  if (!podInfo)
    return (
      <Card>
        <Card.Body>No pod data available</Card.Body>
      </Card>
    );

  // Handle pod status
  const statusColours = {
    Running: "#1B9E3E", // pod running (green)
    true: "#1B9E3E", // container ready = true (green)
    Pending: "#FFC107", // pod pending (yellow)
    false: "#FFC107", // container ready = false (yellow)
    ContainerCreating: "#FFC107", // containerCreating (yellow)
    Terminating: "#FFC107", // pod terminating (yellow)
    Stopped: "#DC3545", // pod stopped (red)
    Failed: "#DC3545", // pod failed (red)
    Unknown: "#6C757D", // pod status unknown (grey)
  };

  // functions to handle pod data
  const getStatusColour = (status) => {
    return statusColours[status] || statusColours["Unknown"];
  };

  const formatDuration = (status, podUptime) => {
    // Check if Terminating
    if (status === "Terminating") {
      return "Pod is terminating";
    }

    // Check if uptime exists & is a string
    if (!podUptime || typeof podUptime !== "string") {
      console.error("Invalid uptime format:", podUptime);
      return "Uptime: Unknown";
    }

    // If not an ISO duration (doesn't start with PT), return as-is
    if (!podUptime.startsWith("PT")) {
      return `Uptime: ${podUptime}`;
    }

    try {
      // Process ISO duration format
      const timeStr = podUptime.replace("PT", "");

      // Extract hours, minutes, seconds
      const hours = parseInt(timeStr.match(/(\d+)H/)?.[1] || 0);
      const minutes = parseInt(timeStr.match(/(\d+)M/)?.[1] || 0);
      const seconds = parseFloat(timeStr.match(/(\d+\.?\d*)S/)?.[1] || 0);

      // Convert to days if more than 24 hours
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;

      const timeFormats = [
        { condition: days > 0, format: () => `${days}d ${remainingHours}h` },
        { condition: hours > 0, format: () => `${hours}h ${minutes}m` },
        {
          condition: minutes > 0,
          format: () => `${minutes}m ${Math.round(seconds)}s`,
        },
      ];

      const format = timeFormats.find(({ condition }) => condition);
      return format ? format.format() : `Uptime: ${Math.round(seconds)}s`;
    } catch (error) {
      // Log error and return 'Unknown' if parsing fails
      console.error("Error parsing duration:", error);
      return "Uptime: Unknown";
    }
  };

  const formatCpu = (cpuValue) => {
    if (!cpuValue) return "0";
    // Remove 'n' and convert to number
    const numericValue = parseInt(cpuValue.replace("n", ""));
    // Convert nanocores to millicores (more commonly used in K8s)
    const millicores = (numericValue / 1000000).toFixed(2);
    return `${millicores}m`;
  };

  const formatCpuDisplay = (cpuString) => {
    if (!cpuString) return "0m";
    const millicores = parseCpuToMillicores(cpuString);
    return `${millicores.toFixed(2)}m`;
  };

  // Convert CPU value to millicores for comparison
  const parseCpuToMillicores = (cpuString) => {
    if (!cpuString) return 0;

    // Handle nanocores (n)
    if (cpuString.endsWith("n")) {
      return parseFloat(cpuString.replace("n", "")) / 1000000;
    }

    // Handle millicores (m)
    if (cpuString.endsWith("m")) {
      return parseFloat(cpuString.replace("m", ""));
    }

    // Handle cores (no unit)
    return parseFloat(cpuString) * 1000; // Convert cores to millicores
  };

  const formatMemory = (memoryValue) => {
    if (!memoryValue) return "0";
    const kibibytes = parseInt(memoryValue.replace("Ki", ""));

    if (kibibytes >= 1048576) {
      // 1 GiB = 1048576 KiB
      return `${(kibibytes / 1048576).toFixed(2)} GiB`;
    } else if (kibibytes >= 1024) {
      // 1 MiB = 1024 KiB
      return `${(kibibytes / 1024).toFixed(2)} MiB`;
    } else {
      return `${kibibytes}Kib`;
    }
  };

  const formatMemoryDisplay = (memoryString) => {
    if (!memoryString) return "0 KiB";
    const kibibytes = parseMemoryToKiB(memoryString);

    if (kibibytes >= 1048576) {
      return `${(kibibytes / 1048576).toFixed(2)} GiB`;
    } else if (kibibytes >= 1024) {
      return `${(kibibytes / 1024).toFixed(2)} MiB`;
    } else {
      return `${kibibytes} KiB`;
    }
  };

  // Convert Memory value to KiB for comparison
  const parseMemoryToKiB = (memoryString) => {
    if (!memoryString) return 0;

    const value = parseFloat(memoryString.replace(/[^0-9.]/g, ""));
    const unit = memoryString.replace(/[0-9.]/g, "").toLowerCase();

    switch (unit) {
      case "gi":
      case "gib":
        return value * 1024 * 1024; // Convert GiB to KiB
      case "mi":
      case "mib":
        return value * 1024; // Convert MiB to KiB
      case "ki":
      case "kib":
        return value;
      default:
        return value;
    }
  };

  const handleContainerSelect = (index) => {
    setSelectedContainer(index);
  };

  // Restart Modal Logic
  const handleRestartModal = () => {
    setShowRestartModal(true);
  };

  const handleRestartClose = () => {
    setShowRestartModal(false);
    setShowPodInfo(false);
  };

  // Scale Modal Logic
  const handleScaleModal = (pod) => {
    setShowScaleModal(true);
  };

  // Logs Modal Logic
  const handleLogsModal = () => {
    console.log("Opening logs modal with data:", {
      podInfo: podInfo,
      selectedContainer: selectedContainer,
    });
    setShowLogsModal(true);
  };

  const podStatus = podInfo.status || "Unknown"; // Default pod status
  const podUptime = formatDuration(podStatus, podInfo.uptime) || "Unknown"; // Default pod uptime

  return (
    <>
      <Row>
        {/* <i
                className="fas fa-times"
                style={{ cursor: 'pointer' }}
                onClick={() => setShowPodInfo(false)}
            ></i> */}

        {/* Pod Summary */}
        <CopyableText
          text={podInfo.pod_name}
          label="Pod Name"
          tooltipText="Click to copy"
          component="h4"
          style={{
            fontSize: "1rem",
            letterSpacing: "0.5px",
            fontWeight: "bold",
            marginBottom: "0",
          }}
        />
        {/* Pod IP */}
        <CopyableText
          text={podInfo.ingress_urls[0]}
          label="Pod URL"
          tooltipText="Click to copy"
          style={{
            color: "#007bff",
            wordBreak: "break-all",
            fontSize: "1rem",
          }}
        />
      </Row>

      {/* Divider */}
      <div
        className="divider"
        style={{
          borderTop: "1px solid #ccc",
          width: "calc(100% + 40px)",
          marginTop: "1.5rem",
          marginBottom: "1.5rem",
          marginLeft: "-20px",
          marginRight: "-20px",
          position: "relative",
        }}
      />

      <Row className="mt-3" style={{ paddingTop: "5px" }}>
        {/* Admin Actions */}
        <Col md={6}>
          <div
            className="admin-actions d-flex justify-content-evenly align-items-center"
            style={{
              backgroundColor: "#fff",
              border: "1px solid #ccc",
              borderRadius: "8px",
              minHeight: "85px",
              height: "100%",
              width: "100%",
            }}
          >
            <div
              className="restart-action d-flex flex-column align-items-center"
              onClick={handleRestartModal}
              style={{
                cursor: "pointer",
                transition: "opacity 0.2s",
                padding: "10px",
              }}
              onMouseOver={(e) => (e.currentTarget.style.opacity = "0.7")}
              onMouseOut={(e) => (e.currentTarget.style.opacity = "1")}
            >
              <i
                className="fa-solid fa-rotate-right mb-2"
                style={{ fontSize: "1.2rem" }}
              ></i>
              <span>Restart</span>
            </div>
            <div
              className="scale-action d-flex flex-column align-items-center"
              onClick={handleScaleModal}
              style={{
                cursor: "pointer",
                transition: "opacity 0.2s",
                padding: "10px",
              }}
              onMouseOver={(e) => (e.currentTarget.style.opacity = "0.7")}
              onMouseOut={(e) => (e.currentTarget.style.opacity = "1")}
            >
              <i
                className="fa-solid fa-layer-group mb-2"
                style={{ fontSize: "1.2rem" }}
              ></i>
              <span>Scale</span>
            </div>
          </div>
        </Col>

        {/* Status & Uptime */}
        <Col md={6}>
          <div
            className="status d-flex flex-column align-items-center justify-content-center p-3"
            style={{
              backgroundColor: "#fff",
              border: "1px solid #ccc",
              borderRadius: "8px",
              minHeight: "85px",
            }}
          >
            <div
              className="status-div"
              style={{
                border: `2px solid ${getStatusColour(podStatus)}`,
                backgroundColor: "white",
                color: getStatusColour(podStatus),
                padding: "4px 12px",
                borderRadius: "16px",
                fontSize: "0.8rem",
                fontWeight: "500",
              }}
            >
              {podStatus}
            </div>
            <div className="mt-2">
              <span>{podUptime}</span>
            </div>
          </div>
        </Col>
      </Row>

      {/* Hardware Util */}
      <Row style={{ paddingTop: "50px" }}>
        <Col md={6}>
          <div className="d-flex flex-column align-items-center">
            <CustomTooltip
              content={
                <div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "4px",
                    }}
                  >
                    <span>
                      Using:{" "}
                      {podInfo.usage.cpu
                        ? formatCpuDisplay(podInfo.usage.cpu)
                        : "??"}
                    </span>
                  </div>
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <span>
                      Quota:{" "}
                      {formatCpuDisplay(
                        podInfo.containers[0].resources.requests.cpu
                      )}
                    </span>
                  </div>
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <span>
                      Remaining:{" "}
                      {podInfo.usage.cpu &&
                      podInfo.containers[0].resources.requests.cpu
                        ? formatCpuDisplay(
                            `${
                              parseCpuToMillicores(
                                podInfo.containers[0].resources.requests.cpu
                              ) *
                                1000000 -
                              parseCpuToMillicores(podInfo.usage.cpu) * 1000000
                            }n`
                          )
                        : "N/A"}
                    </span>
                  </div>
                </div>
              }
            >
              <div
                className="d-flex flex-column align-items-center"
                style={{ width: "100%" }}
              >
                <span>CPU</span>
                <ProgressBar
                  now={
                    podInfo.usage.cpu
                      ? parseCpuToMillicores(podInfo.usage.cpu)
                      : 0
                  }
                  max={
                    podInfo.containers[0].resources.requests.cpu
                      ? parseCpuToMillicores(
                          podInfo.containers[0].resources.requests.cpu
                        )
                      : 100
                  }
                  style={{ width: "80%" }}
                />
              </div>
            </CustomTooltip>
          </div>
        </Col>
        <Col md={6}>
          <div className="d-flex flex-column align-items-center">
            <CustomTooltip
              content={
                <div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "4px",
                    }}
                  >
                    <span>
                      Using:{" "}
                      {podInfo.usage.memory
                        ? formatMemoryDisplay(podInfo.usage.memory)
                        : "??"}
                    </span>
                  </div>
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <span>
                      Quota:{" "}
                      {formatMemoryDisplay(
                        podInfo.containers[0].resources.requests.memory
                      )}
                    </span>
                  </div>
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <span>
                      Remaining:{" "}
                      {podInfo.usage.memory &&
                      podInfo.containers[0].resources.requests.memory
                        ? formatMemoryDisplay(
                            `${
                              parseMemoryToKiB(
                                podInfo.containers[0].resources.requests.memory
                              ) - parseMemoryToKiB(podInfo.usage.memory)
                            }Ki`
                          )
                        : "N/A"}
                    </span>
                  </div>
                </div>
              }
            >
              <div
                className="d-flex flex-column align-items-center"
                style={{ width: "100%" }}
              >
                <span>Memory</span>
                <ProgressBar
                  now={
                    podInfo.usage.memory
                      ? parseMemoryToKiB(podInfo.usage.memory)
                      : 0
                  }
                  max={
                    podInfo.containers[0].resources.requests.memory
                      ? parseMemoryToKiB(
                          podInfo.containers[0].resources.requests.memory
                        )
                      : 100
                  }
                  style={{ width: "80%" }}
                />
              </div>
            </CustomTooltip>
          </div>
        </Col>
      </Row>

      {/* Containers List */}
      <Row style={{ paddingTop: "30px" }}>
        <Col md={12}>
          <div
            className="container-list d-flex flex-column align-items-center"
            style={{ gap: "4px" }}
          >
            {podInfo.containers &&
              podInfo.containers.map((container, index) => (
                <div
                  key={index}
                  className="container w-100"
                  style={{
                    backgroundColor: "#fff",
                    padding: "8px 16px",
                    borderRadius: "5px",
                    border:
                      selectedContainer === index
                        ? "2px solid #444"
                        : "1px solid #ccc",
                    fontSize: "0.8rem",
                    fontWeight: "500",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    transition: "opacity 0.2s",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                  onClick={() => handleContainerSelect(index)}
                >
                  <div className="container-name">
                    {index === 0 && (
                      <span className="me-2" style={{ color: "#0072ce" }}>
                        Deployment
                      </span>
                    )}
                    {container.name}
                  </div>
                  <div className="continer-hw-info d-flex gap-1">
                    {container.resources.limits.cpu && (
                      <div className="d-flex align-items-center">
                        <i
                          className="fa-solid fa-server"
                          style={{ marginRight: "4px", color: "blue" }}
                        ></i>
                        {`${formatCpu(container.resources.limits.cpu)}`}
                      </div>
                    )}
                    {container.resources.limits.memory && (
                      <div className="d-flex align-items-center">
                        <i
                          className="fa-solid fa-microchip"
                          style={{ marginRight: "4px", color: "#FFC107" }}
                        ></i>
                        {`${formatMemory(container.resources.limits.memory)}`}
                      </div>
                    )}
                    {container.resources.limits["nvidia.com/gpu"] && (
                      <div className="d-flex align-items-center">
                        <i
                          className="fa-solid fa-desktop"
                          style={{ marginRight: "4px", color: "green" }}
                        ></i>
                        {`${parseInt(
                          container.resources.limits["nvidia.com/gpu"]
                        )} GPUs`}
                      </div>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </Col>
      </Row>

      {/* Logs */}
      <Row>
        <Col md={12} className="mt-4">
          <div
            className="logs"
            style={{
              border: "1px solid #ccc",
              borderRadius: "8px",
              minHeight: "85px",
              filter: showLogsModal ? "blur(2px)" : "none",
              transition: "filter 0.2s ease",
            }}
          >
            <div
              className="logs-title d-flex justify-content-between align-items-center p-1"
              style={{ borderBottom: "1px solid #ccc" }}
            >
              <p className="m-0">Logs</p>
              <i
                className="fa-solid fa-arrow-up-right-from-square"
                style={{
                  cursor: "pointer",
                  transition: "opacity 0.2s",
                }}
                onClick={handleLogsModal}
              ></i>
            </div>
            <LogsViewer
              logs={podInfo.containers[selectedContainer]?.logs}
              containerIndex={selectedContainer}
              height="300px"
              scrollableTargetId={`pod-logs-${selectedContainer}`}
            />
          </div>
        </Col>
      </Row>

      {/* Restart Modal */}
      <RestartModal
        showRestartModal={showRestartModal}
        handleRestartClose={handleRestartClose}
        podInfo={podInfo}
        cluster={cluster}
        onRestartSuccess={refreshPodData}
      />

      {/* Scale Modal */}
      <ScaleModal
        showScaleModal={showScaleModal}
        handleScaleClose={() => setShowScaleModal(false)}
        podInfo={podInfo}
        cluster={cluster}
        onScaleSuccess={refreshPodData}
      />

      {/* Logs Modal */}
      <LogsModal
        showLogsModal={showLogsModal}
        handleLogsClose={() => setShowLogsModal(false)}
        podInfo={podInfo}
        cluster={cluster}
        selectedContainer={selectedContainer}
      />
    </>
  );
};

export default PodInfo;
